/**
 * LivePreviewPage — /builder/:id/live
 *
 * The generated website with NO builder chrome, rendered via the Builder Engine. It
 * fetches the persisted builder from the backend by id (the single source of truth).
 *
 * When embedded in the Builder workspace preview iframe, the parent streams the live
 * working schema via same-origin postMessage, so the preview stays live while editing
 * AND renders in a real viewport (correct responsive typography/breakpoints). Opened
 * standalone ("View live" / a new tab), no messages arrive and it behaves exactly as
 * before — refresh/new tab reload from the backend.
 */
import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BuilderEngine, SB_SCROLL_TO_SECTION, SB_ACTIVE_SECTION, scrollToSection, observeActiveSection } from '../site-builder/runtime'
import type { SiteSchema } from '../site-builder/schema'
import { themeOverrideVars } from '../site-builder/components/shared/theme/themeVars'
import { applyBusinessInfoToContent } from './builder/editorContext'
import { siteBuilderApi } from '../api/client'
import { builderRegistry, builderConfig } from './previewRuntime'

export function LivePreviewPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['builder', id],
    queryFn: () => siteBuilderApi.getBuilder(id ?? ''),
    enabled: !!id,
    retry: 1,
  })

  // Live working schema pushed by the parent workspace (when embedded).
  const [override, setOverride] = useState<SiteSchema | null>(null)
  // Per-field CSS-var overrides for the edited theme fields (empty = reference default).
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({})
  const raw = override ?? data?.schema ?? null

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const d = e.data as { type?: string; schema?: SiteSchema; themeVars?: Record<string, string>; sectionId?: string } | null
      if (d && d.type === 'sb-preview-schema' && d.schema) {
        setOverride(d.schema)
        setPreviewVars(d.themeVars ?? {})
      }
      // Section Navigation UX: the sidebar asks the preview to smooth-scroll to a section by
      // its existing, stable schema id (see `previewSync.ts` — no fragile DOM querying).
      if (d && d.type === SB_SCROLL_TO_SECTION && d.sectionId) scrollToSection(d.sectionId)
    }
    window.addEventListener('message', onMessage)
    // Announce readiness so the parent pushes the current schema (covers load races).
    window.parent?.postMessage({ type: 'sb-preview-ready' }, window.location.origin)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Mark this document as a full-page site so the global height:100% cap on
  // html/body/#root is lifted — the viewport scrolls and the sticky navbar's
  // containing block spans the full page (restores sticky nav). See index.css.
  useEffect(() => {
    document.documentElement.classList.add('sb-live')
    return () => document.documentElement.classList.remove('sb-live')
  }, [])

  // Begin at the hero (only on first load / id change — not on every live edit).
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  // Section Navigation UX: report whichever section is currently in view so the sidebar's
  // highlight stays in sync with manual scrolling too, not just clicks. Re-observes
  // whenever the CURRENT section id order changes (reorder, show/hide, or any edit) — ids
  // are stable, so re-querying by id always finds the right elements regardless of order.
  const sectionIds = raw?.pages[0]?.sections.map((s) => s.id).join(',') ?? ''
  useEffect(() => {
    const ids = sectionIds ? sectionIds.split(',') : []
    return observeActiveSection(ids, (sectionId) => {
      window.parent?.postMessage({ type: SB_ACTIVE_SECTION, sectionId }, window.location.origin)
    })
  }, [sectionIds])

  if (isLoading && !override) {
    return <div className="grid min-h-screen place-items-center text-sm text-gray-400">Loading preview…</div>
  }
  if (!raw) {
    return <div className="grid min-h-screen place-items-center text-sm text-gray-400">Preview unavailable.</div>
  }

  // Mirror persisted businessInfo → rendered section content so a reloaded/standalone
  // saved site shows the persisted social/contact edits (no-op when businessInfo absent).
  const schema = applyBusinessInfoToContent(raw)

  // Theme CSS vars applied at the preview root:
  //  - embedded  → the per-field overrides streamed by the workspace (empty = reference)
  //  - standalone manual-saved site → ONLY the fields that differ from the reference,
  //    so reload matches the editor exactly (reference default + just the user's edits).
  //    `primaryColor` (→ --sb-primary) and `brandColor` (→ --sb-brand) are independent
  //    fields diffed independently — editing one never emits the other's variable.
  //  - standalone AI-generated site → nothing (renders the pixel-perfect reference default)
  const themeStyle: Record<string, string> =
    override
      ? previewVars
      : schema.metadata.source === 'manual'
        ? themeOverrideVars(schema.theme, schema.branding.brandColor)
        : {}

  const rootStyle = { ...themeStyle } as CSSProperties
  return (
    <div style={rootStyle}>
      <BuilderEngine schema={schema} registry={builderRegistry} config={builderConfig} />
    </div>
  )
}
